import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Header } from 'mlb-react-header';
import {
  getDefaultHeaderMenu,
  getMlbHeaderTheme,
  FieldPassChangeReasons,
  FieldPassChangeEvent,
} from '@mlb/play-ui';
import { Skeleton } from '@gif/shared-ui';
import { getLang, LooseObject } from '@gif/shared-global';
import { useUser } from '@gif/shared-rtk';
import { useGetHeaderDataQuery, useGetSharedNavQuery } from '../../api';
import { fieldPassConfig } from '../../utils';
import { useAppSelector, useAppDispatch } from '../../store';

const { LOGIN_SUCCESS, REGISTER_SUCCESS } = FieldPassChangeReasons;

const lang = getLang();

export function MlbHeader() {
  const { user, setUser, loadUser } = useUser({
    useAppSelector,
    useAppDispatch,
  });
  const theme = useTheme() as any;
  const { data, isLoading } = useGetHeaderDataQuery({ lang });
  const { data: sharedNav, isLoading: isLoadingSharedNav } =
    useGetSharedNavQuery({ lang });
  const menuItems = data?.menuItems || getDefaultHeaderMenu();
  const handleLogOut = async () => {
    try {
      // `logout` will automatically refresh the page
      await window.mlbOkta.logout();
    } catch (e) {
      // Probably don't want to crash the entire site with this
      console.error(e);
    }
  };
  const onFieldPassLoginSuccessHandler = useCallback(
    (event: FieldPassChangeEvent) => {
      // @TODO - tracking
      setUser({
        ...event.data,
        loggedIn: true,
      });
    },
    [setUser]
  );
  const onFieldPassRegisterSuccessHandler = useCallback(() => {
    // @TODO - tracking
    // we are doing this because user data is not available to us in the event on successful registration
    loadUser();
  }, [loadUser]);
  const FieldPassChangeReasonMapHandler: LooseObject = useMemo(
    () => ({
      [LOGIN_SUCCESS]: onFieldPassLoginSuccessHandler,
      [REGISTER_SUCCESS]: onFieldPassRegisterSuccessHandler,
    }),
    [onFieldPassLoginSuccessHandler, onFieldPassRegisterSuccessHandler]
  );
  const onAuthSuccessHandler = useCallback(
    (event: FieldPassChangeEvent) => {
      FieldPassChangeReasonMapHandler[event.reason]?.(event);
    },
    [FieldPassChangeReasonMapHandler]
  );
  const sharedNavJson = useMemo(() => {
    if (!sharedNav) {
      return;
    }
    return JSON.parse(sharedNav);
  }, [sharedNav]);

  if (isLoading || isLoadingSharedNav) {
    return <Skeleton height="3rem" />;
  }

  // Header menu will be mostly empty until loading is done.
  // At which point it'll either get the global header or use default header
  return (
    <section>
      <Header
        contentfulProps={{ palette: getMlbHeaderTheme(theme.id) }}
        isLoggedIn={user.loggedIn}
        onLogout={handleLogOut}
        useInlineLogout={true}
        useFieldPass={true}
        fieldPassConfig={fieldPassConfig}
        onAuthSuccess={onAuthSuccessHandler}
        headerData={{ menuItems }}
        locale={lang}
        sharedNav={sharedNavJson}
      />
    </section>
  );
}

export default MlbHeader;
